import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  WarningOctagonIcon,
  CheckIcon,
  ImageIcon,
  TextAaIcon,
  FileTextIcon,
  TagIcon,
  MapPinIcon,
  CaretLeftIcon,
  CaretRightIcon
} from '@phosphor-icons/react';
import { ValidationError } from '@/utils/publishValidation';
import LureoButton from '@/common/LureoButton';
import { ListRow } from '@/components/ui/list-row';
import { Note } from '@/components/ui/note';
import { IconButton } from '@/components/ui/icon-button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/cms/ui/alert-dialog";

interface MissingInfoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCancel: () => void;
  errors: ValidationError[];
  projectName?: string;
  projectId?: string;
  sceneOriginId?: string;
}

export function MissingInfoDialog({
  open,
  onOpenChange,
  onCancel,
  errors,
  projectName = 'this project',
  projectId,
  sceneOriginId
}: MissingInfoDialogProps) {
  const [currentSceneIndex, setCurrentSceneIndex] = useState(0);
  const navigate = useNavigate();

  const handleCancel = () => {
    onCancel();
    onOpenChange(false);
  };

  const handleFieldClick = (field: string, errorSceneOriginId?: string) => {
    if (!projectId) return;

    // Use sceneOriginId from error data if available, otherwise fall back to prop
    const effectiveSceneOriginId = errorSceneOriginId || sceneOriginId;

    // Close the dialog first
    onOpenChange(false);

    // Navigate based on field type - just add ?highlight to trigger validation mode
    if (field.startsWith('project_')) {
      // Project fields - go to project edit page
      navigate(`/project/edit/${projectId}?highlight`);
    } else if (field.startsWith('scene_')) {
      // Scene fields - go to scene edit page
      if (effectiveSceneOriginId) {
        navigate(`/story/edit/${effectiveSceneOriginId}?highlight`);
      } else {
        navigate(`/project/edit/${projectId}?highlight`);
      }
    } else {
      // Fallback to project edit page
      navigate(`/project/edit/${projectId}?highlight`);
    }
  };

  // Group errors by category
  const projectErrors = errors.filter(error => error.category === 'project');
  const sceneErrors = errors.filter(error => error.category === 'scene');

  // Group scene errors by sceneOriginId for pagination
  const sceneErrorsByOrigin = sceneErrors.reduce((acc, error) => {
    const sceneId = error.sceneOriginId || 'unknown';
    if (!acc[sceneId]) {
      acc[sceneId] = [];
    }
    acc[sceneId].push(error);
    return acc;
  }, {} as Record<string, ValidationError[]>);

  const sceneOriginIds = Object.keys(sceneErrorsByOrigin);
  const totalScenes = sceneOriginIds.length;
  const hasMultipleScenes = totalScenes > 1;
  const currentSceneId = sceneOriginIds[currentSceneIndex];
  const currentSceneErrors = currentSceneId ? sceneErrorsByOrigin[currentSceneId] : [];

  const goToPreviousScene = () => {
    if (currentSceneIndex > 0) {
      setCurrentSceneIndex(currentSceneIndex - 1);
    }
  };

  const goToNextScene = () => {
    if (currentSceneIndex < totalScenes - 1) {
      setCurrentSceneIndex(currentSceneIndex + 1);
    }
  };

  // Reset pagination when dialog opens
  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      setCurrentSceneIndex(0);
    }
    onOpenChange(newOpen);
  };


  const getFieldDisplayName = (field: string): string => {
    const fieldNames: Record<string, string> = {
      'project_name': 'Project Name',
      'project_source_name': 'Creator Name',
      'project_description': 'Project Description',
      'project_tags': 'Project Tags',
      'project_images': 'Project Images',
      'project_portrait_image': 'Project Portrait Image',
      'project_landscape_image': 'Project Landscape Image',
      'scene_name': 'Scene Name',
      'scene_location': 'Scene Location',
      'scene_description': 'Scene Description',
      'scene_cover_image': 'Scene Cover Image',
      'scene_scan_image': 'Scan Image',
      'scene_anchor_image': 'Anchor Image',
      'scene_anchor': 'Anchor',
      'scene_devices': 'Platforms'
    };
    return fieldNames[field] || field;
  };

  const getFieldIcon = (field: string) => {
    if (field.includes('image')) {
      return <ImageIcon />;
    } else if (field.includes('name')) {
      return <TextAaIcon />;
    } else if (field.includes('description')) {
      return <FileTextIcon />;
    } else if (field.includes('tags')) {
      return <TagIcon />;
    } else if (field.includes('location')) {
      return <MapPinIcon />;
    }
    return <WarningOctagonIcon />;
  };


  if (!open) return null;

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent width="lg">
        <AlertDialogHeader>
          <AlertDialogTitle>Missing Required Information</AlertDialogTitle>
          <AlertDialogDescription>
            Before publishing {projectName}, please complete the following required fields:
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogBody className="gap-6">
          {projectErrors.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-lureo-14 font-semibold text-lureo-ink flex items-center gap-2 leading-5">
                Project Information
              </h3>
              <div className="space-y-2">
                {projectErrors.map((error, index) => (
                  <ListRow
                    key={index}
                    icon={getFieldIcon(error.field)}
                    label={getFieldDisplayName(error.field)}
                    description={error.message}
                    onClick={() => handleFieldClick(error.field, error.sceneOriginId)}
                  />
                ))}
              </div>
            </div>
          )}

          {sceneErrors.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lureo-14 font-semibold text-lureo-ink flex items-center gap-2 leading-5">
                  Scene Information
                </h3>
                {hasMultipleScenes && (
                  <div className="flex items-center gap-2">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <IconButton label="Previous scene" onClick={goToPreviousScene} disabled={currentSceneIndex === 0}>
                          <CaretLeftIcon className="h-4 w-4" />
                        </IconButton>
                      </TooltipTrigger>
                      <TooltipContent sideOffset={10}>Previous scene</TooltipContent>
                    </Tooltip>
                    <div className="text-lureo-14 text-lureo-ink font-medium tabular-nums min-w-12 text-center leading-5">
                      <div>{currentSceneIndex + 1} / {totalScenes}</div>
                    </div>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <IconButton label="Next scene" onClick={goToNextScene} disabled={currentSceneIndex === totalScenes - 1}>
                          <CaretRightIcon className="h-4 w-4" />
                        </IconButton>
                      </TooltipTrigger>
                      <TooltipContent sideOffset={10}>Next scene</TooltipContent>
                    </Tooltip>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                {currentSceneErrors.map((error, index) => (
                  <ListRow
                    key={index}
                    icon={getFieldIcon(error.field)}
                    label={getFieldDisplayName(error.field)}
                    description={error.message}
                    onClick={() => handleFieldClick(error.field, error.sceneOriginId)}
                  />
                ))}
              </div>
            </div>
          )}

          <Note icon={<CheckIcon />}>
            <div className="font-medium mb-1">Next Steps:</div>
            <div className="space-y-1">
              <div>• Complete the missing information above</div>
              <div>• Make sure to save your changes before publishing</div>
              <div>• Try publishing again once all required fields are filled</div>
            </div>
          </Note>
        </AlertDialogBody>

        <AlertDialogFooter>
          <LureoButton
            variant="outline-2"
            suppressTooltip
            onClick={handleCancel}
            className="h-9 px-5 rounded-lureo-radius-button-pill"
          >
            Cancel
          </LureoButton>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
